import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// ⚠️ MOCK MODE - Set to false to use real OTP APIs
const USE_MOCK_OTP = false;
const MOCK_OTP_CODE = '123456';

/**
 * OTP Service for two-step verification
 */
export const OtpService = {
  /**
   * Send OTP to mobile number
   * @param {string} mobileNumber - Mobile number in format 94XXXXXXXXX
   * @param {string} instituteId - Institute ID (optional)
   * @returns {Promise<Object>} Response with success status and data
   */
  sendOtp: async (mobileNumber, instituteId = null) => {
    // MOCK OTP MODE - Remove this block to use real API
    if (USE_MOCK_OTP) {
      return {
        success: true,
        message: `📱 Mock OTP sent to ${mobileNumber}. Test code: ${MOCK_OTP_CODE}`,
        data: {
          requestId: 'mock_' + Date.now(),
          expiresIn: 600
        }
      };
    }

    // REAL OTP API - Uncomment below to use actual OTP service
    try {
      const payload = {
        MobileNumber: mobileNumber,
        ...(instituteId && { InstituteId: instituteId })
      };

      const response = await axios.post(`${API_BASE_URL}/otp-v2`, payload);

      // Check for success response
      if (response.data?.resCode === 200 && response.data?.status === 1000) {
        return {
          success: true,
          message: response.data.message || 'OTP sent successfully',
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to send OTP',
          error: response.data?.error || 'SEND_FAILED'
        };
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      
      // Handle error response
      const errorMessage = error.response?.data?.message || 'Failed to send OTP. Please try again.';
      const errorCode = error.response?.data?.error || 'NETWORK_ERROR';
      
      return {
        success: false,
        message: errorMessage,
        error: errorCode
      };
    }
  },

  /**
   * Verify OTP code
   * @param {string} mobileNumber - Mobile number in format 94XXXXXXXXX
   * @param {string} otpValue - 6-digit OTP code
   * @returns {Promise<Object>} Response with success status and data
   */
  verifyOtp: async (mobileNumber, otpValue) => {
    // MOCK OTP MODE - Remove this block to use real API
    if (USE_MOCK_OTP) {
      if (otpValue === MOCK_OTP_CODE) {
        return {
          success: true,
          message: 'OTP verified successfully',
          data: {
            sessionId: 'mock_session_' + Date.now(),
            token: 'mock_token_' + Math.random().toString(36).substr(2, 9),
            customerId: 'mock_customer'
          }
        };
      } else {
        return {
          success: false,
          message: `Invalid OTP. Test code is: ${MOCK_OTP_CODE}`,
          error: 'INVALID_OTP'
        };
      }
    }

    // REAL OTP API - Uncomment below to use actual OTP service
    try {
      const payload = {
        MobileNumber: mobileNumber,
        OTPValue: otpValue
      };

      const response = await axios.post(`${API_BASE_URL}/verify-otp-v2`, payload);

      // Check for success response
      if (response.data?.resCode === 200 && response.data?.status === 1000) {
        return {
          success: true,
          message: response.data.message || 'OTP verified successfully',
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Invalid OTP. Please try again.',
          error: response.data?.error || 'VERIFICATION_FAILED'
        };
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      
      // Handle error response
      const errorMessage = error.response?.data?.message || 'Failed to verify OTP. Please try again.';
      const errorCode = error.response?.data?.error || 'NETWORK_ERROR';
      
      return {
        success: false,
        message: errorMessage,
        error: errorCode
      };
    }
  }
};

export default OtpService;
